/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, take, race, call, all, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { Ducks, XError, DisabledFeaturesValues } from '@wit/vtv-x-foundation-lib';
import {
  Creators as RegionActionsCreators,
  Selectors as RegionSelectors,
  ActionTypes as RegionActionTypes,
} from 'core/store/ducks/region';
import { Creators as ChannelsActionsCreators, ActionTypes as ChannelsActionTypes } from 'core/store/ducks/epg/channels';
import {
  Creators as TunedChannelsActionsCreators,
  ActionType as TunedChannelsActionType,
} from 'core/store/ducks/epg/tunedChannels';
import { ActionCreators as WishlistActionsCreators, ActionType as WishlistActionType } from 'core/store/ducks/wishlist';
import { ActionCreators as EntitlementsActionCreators } from 'core/store/ducks/entitlements';
import {
  ActionCreators as PpvEntitlementActionCreators,
  ActionTypes as PpvEntitlementActionType,
} from 'core/store/ducks/entitlements/ppv';
import {
  ActionCreators as SubscriptionEntitlementActionCreators,
  ActionTypes as SubscriptionEntitlementActionType,
} from 'core/store/ducks/entitlements/subscription';
import {
  ActionCreators as CollectionEntitlementActionCreators,
  ActionTypes as CollectionEntitlementActionType,
} from 'core/store/ducks/entitlements/collection';

import { ActionCreators as RecordingsActionCreators } from 'core/store/ducks/recordings';
import {
  ActionCreators as ContentSettingsActionCreators,
  ActionTypes as ContentSettingsActionTypes,
} from 'core/store/ducks/contentSettings';

import { ActionCreators as AuthNActionCreators } from 'core/store/ducks/playbackLicense/authN';
import AppConfig from '../../../services/AppConfig';

type ErrorResult = { error?: XError };
const NO_ERROR = { error: undefined };

function* loadChannelsData(): SagaIterator<ErrorResult> {
  const isSsmEnabled: boolean = yield select(Ducks.Dms.Selectors.isSsmEnabled);

  if (!isSsmEnabled) {
    yield put(AuthNActionCreators.init());
  }

  const regionalizationEnabled = yield select(RegionSelectors.isEnabled);

  if (regionalizationEnabled) {
    yield put(RegionActionsCreators.loadRegion());
    const { error: loadRegionFail } = yield race({
      success: take(RegionActionTypes.SUCCESS),
      error: take(RegionActionTypes.FAIL),
    });

    if (loadRegionFail) {
      return { error: loadRegionFail.error };
    }
  }

  yield put(ChannelsActionsCreators.loadChannels());
  const { error: loadChannelsFail } = yield race({
    success: take(ChannelsActionTypes.SUCCESS),
    error: take(ChannelsActionTypes.FAIL),
  });

  if (loadChannelsFail) {
    return { error: loadChannelsFail.error };
  }

  yield put(TunedChannelsActionsCreators.init());
  yield take(TunedChannelsActionType.INIT_DONE);

  return NO_ERROR;
}

function* loadWishlist(): SagaIterator {
  yield put(WishlistActionsCreators.getItems());
  yield race([take(WishlistActionType.GET_ITEMS_SUCCESS), take(WishlistActionType.GET_ITEMS_ERROR)]);
}

function* loadHouseholdPpvEntitlements(): SagaIterator {
  yield put(PpvEntitlementActionCreators.request());
  yield race([take(PpvEntitlementActionType.SUCCESS), take(PpvEntitlementActionType.FAIL)]);
}

function* loadHouseholdSubscriptionEntitlements(): SagaIterator {
  yield put(SubscriptionEntitlementActionCreators.request());
  yield race([take(SubscriptionEntitlementActionType.SUCCESS), take(SubscriptionEntitlementActionType.FAIL)]);
}

function* loadHouseholdCollectionEntitlements(): SagaIterator {
  yield put(CollectionEntitlementActionCreators.request());
  yield race([take(CollectionEntitlementActionType.SUCCESS), take(CollectionEntitlementActionType.FAIL)]);
}

function* scheduleHouseholdEntitlementsCachingRefresh(): SagaIterator {
  yield put(EntitlementsActionCreators.scheduleCachingRefresh());
}

function* loadHouseholdEntitlements(): SagaIterator {
  yield all([
    call(loadHouseholdPpvEntitlements),
    call(loadHouseholdSubscriptionEntitlements),
    call(loadHouseholdCollectionEntitlements),
    call(scheduleHouseholdEntitlementsCachingRefresh),
  ]);
}

function* loadRecordings(): SagaIterator {
  const isRecordingsDisabled: boolean = yield select(
    Ducks.Dms.Selectors.isFeatureDisabled,
    DisabledFeaturesValues.NO_PVR
  );

  if (!isRecordingsDisabled) {
    yield put(RecordingsActionCreators.load());
  }

  // the app will not wait until recording be loaded
  // recordings retrieving remains in backgroung
}

function* loadSettings(): SagaIterator {
  yield put(ContentSettingsActionCreators.loadShowUnsubscribedChannels());
  yield race([
    take(ContentSettingsActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS_SUCCESS),
    take(ContentSettingsActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS_FAIL),
  ]);
}

function* loadPaymentData(): SagaIterator {
  if (AppConfig.env.FEATURE_PURCHASES_ENABLED) {
    yield put(Ducks.Household.Creators.loadPaymentData());
  }
}

function* loadData(): SagaIterator<ErrorResult> {
  yield put(Ducks.Menu.ActionCreators.getMenu());

  const { error: getMenuFail } = yield race({
    success: take(Ducks.Menu.ActionTypes.SUCCESS),
    error: take(Ducks.Menu.ActionTypes.FAIL),
  });

  if (getMenuFail) {
    return { error: getMenuFail.error };
  }

  const [channelsResult] = yield all([
    call(loadChannelsData),
    call(loadWishlist),
    call(loadHouseholdEntitlements),
    call(loadRecordings),
    call(loadPaymentData),
  ]);

  // only the channels result is important in the startup stage because they are mandatory
  // for other data errors handling it is not important in this stage
  return channelsResult;
}

export function* load(): SagaIterator<ErrorResult> {
  const res: ErrorResult = yield call(loadData);
  if (res.error) {
    return res;
  }

  yield call(loadSettings);

  return NO_ERROR;
}
